import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  ShoppingCart, 
  ArrowLeft, 
  MapPin, 
  Save,
  Calculator,
  Package,
  TrendingUp,
  CheckCircle,
  Eye,
  XCircle
} from 'lucide-react';
import { X } from 'lucide-react';
import { db, getDocuments } from '../services/firebase';
import { getFilteredOutletsForUser } from '../utils/outletFiltering';
import { getSalesDetailsForOutlet, saveSaleTransaction } from '../services/salesTransactions';
import { createOutletVisit, getOutletVisitsForRoute, updateOutletVisitWithTransaction } from '../services/outletVisits';
import { useAuth } from '../contexts/AuthContext';
import { Outlet, SalesDetail, OutletVisit } from '../types';
import NoSaleReasonModal from './sales/NoSaleReasonModal';
import { collection, onSnapshot, query, where, getDocs, orderBy } from 'firebase/firestore';

interface SalesProps {
  onBack: () => void;
  // If provided, Sales will start directly with this outlet selected
  initialOutlet?: Outlet | null;
  // Edit mode data with existing sale details
  editMode?: {
    outlet: any;
    transactionId: string;
    salesDetails: any[];
    discount: number;
  } | null;
}

const Sales: React.FC<SalesProps> = ({ onBack, initialOutlet, editMode }) => {
  const { currentUser } = useAuth();
  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [selectedOutlet, setSelectedOutlet] = useState<Outlet | null>(null);
  const [salesDetails, setSalesDetails] = useState<SalesDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingSalesDetails, setLoadingSalesDetails] = useState(false);
  const [userRoute, setUserRoute] = useState<any>(null);
  const [routeError, setRouteError] = useState<string>('');
  const [debounceTimers, setDebounceTimers] = useState<{[key: string]: NodeJS.Timeout}>({});
  const [outletVisits, setOutletVisits] = useState<{ [outletId: string]: OutletVisit }>({});
  const [visitStats, setVisitStats] = useState({ visited: 0, total: 0 });
  const [showNoSaleModal, setShowNoSaleModal] = useState(false);
  // Route/van inventory (real-time) and visibility control
  const [routeInventory, setRouteInventory] = useState<{ [productId: string]: number }>({});
  const [visibleProductIds, setVisibleProductIds] = useState<Set<string>>(new Set());
  const [initializedVisibleFromInventory, setInitializedVisibleFromInventory] = useState(false);
  const [showAddItemPicker, setShowAddItemPicker] = useState(false);
  const [addItemSearch, setAddItemSearch] = useState('');
  // Pricing data preloaded from warehouse_daily_pricing
  const [todayPricing, setTodayPricing] = useState<{ [productId: string]: number }>({});
  const [pricingLoading, setPricingLoading] = useState(true);
  // Additional discount field
  const [additionalDiscount, setAdditionalDiscount] = useState<number>(editMode?.discount || 0);

  useEffect(() => {
    loadData();
  }, []);

  // If an initial outlet or edit mode is provided, pre-select it and load sales details
  useEffect(() => {
    const preselect = async () => {
      const targetOutlet = editMode?.outlet || initialOutlet;
      if (!targetOutlet) return;
      
      setSelectedOutlet(targetOutlet);
      setLoadingSalesDetails(true);
      
      try {
        const today = new Date().toISOString().split('T')[0];
        
        if (editMode) {
          // Edit mode: Load existing sales details
          console.log('✏️ Loading edit mode data for outlet:', targetOutlet.name);
          setSalesDetails(editMode.salesDetails);
        } else {
          // New sale mode: Record visit and load fresh sales details
          if (currentUser?.id && userRoute?.routeId) {
            try {
              const visitResult = await createOutletVisit(
                targetOutlet.id,
                targetOutlet.name || 'Unknown Outlet',
                currentUser.id,
                currentUser.name || 'Unknown Salesman',
                userRoute.routeId,
                today
              );
              if (visitResult.success) {
                setOutletVisits(prev => ({
                  ...prev,
                  [targetOutlet.id]: {
                    id: visitResult.visitId || '',
                    date: today,
                    routeId: userRoute.routeId,
                    outletId: targetOutlet.id,
                    outletName: targetOutlet.name || 'Unknown Outlet',
                    salesmanId: currentUser.id,
                    salesmanName: currentUser.name || 'Unknown Salesman',
                    visitStartTime: new Date(),
                    status: 'visit_only'
                  }
                }));
              }
            } catch (err) {
              console.warn('Failed to create preselected visit record:', err);
            }
          }
          const details = await getSalesDetailsForOutlet(targetOutlet.id, today, todayPricing);
          setSalesDetails(details);
        }
      } catch (e) {
        console.error('Error loading sales details for outlet:', e);
        setSalesDetails([]);
      } finally {
        setLoadingSalesDetails(false);
      }
    };
    preselect();
  }, [initialOutlet, editMode, currentUser?.id, currentUser?.name, userRoute?.routeId, todayPricing]);

  // Preload today's pricing for user's warehouse - NO FALLBACKS
  const loadTodaysPricing = useCallback(async () => {
    if (!currentUser?.warehouseId) {
      console.log('⚠️ No warehouseId for current user, cannot load pricing');
      setPricingLoading(false);
      return;
    }

    try {
      const today = new Date().toISOString().split('T')[0];
      console.log(`💰 Loading pricing for warehouse: ${currentUser.warehouseId}, date: ${today}`);
      
      const pricingQuery = query(
        collection(db, 'warehouse_daily_pricing'),
        where('warehouseId', '==', currentUser.warehouseId),
        where('date', '==', today),
        where('isActive', '==', true)
      );
      
      const pricingSnapshot = await getDocs(pricingQuery);
      const pricingData: { [productId: string]: number } = {};
      
      pricingSnapshot.forEach(doc => {
        const pricing = doc.data();
        pricingData[pricing.productId] = pricing.unitRate || 0;
        console.log(`💰 Loaded pricing: ${pricing.productName} = ₹${pricing.unitRate}`);
      });
      
      setTodayPricing(pricingData);
      console.log(`✅ Loaded pricing for ${Object.keys(pricingData).length} products`);
      
    } catch (error) {
      console.error('❌ Error loading pricing:', error);
    } finally {
      setPricingLoading(false);
    }
  }, [currentUser?.warehouseId]);

  useEffect(() => {
    loadTodaysPricing();
  }, [loadTodaysPricing]);

  const loadData = async () => {
    try {
      console.log('🔍 Loading route-based outlets and visit data...');
      
      if (!currentUser?.id) {
        setRouteError('User not authenticated');
        setLoading(false);
        return;
      }

      // Use shared outlet filtering function - show only ACTIVE outlets in Sales
      const result = await getFilteredOutletsForUser(currentUser.id, 'active');
      
      if (result.error) {
        setRouteError(result.error);
        setLoading(false);
        return;
      }

      // Load visit data for visual indicators
      const today = new Date().toISOString().split('T')[0];
  const visits = await getOutletVisitsForRoute(result.userRoute.routeId, today, currentUser.id);
      const visitMap: { [outletId: string]: OutletVisit } = {};
      visits.forEach(visit => {
        visitMap[visit.outletId] = visit;
      });

      setOutlets(result.outlets);
      setUserRoute(result.userRoute);
      setOutletVisits(visitMap);
      setVisitStats({
        visited: visits.length,
        total: result.outlets.length
      });
      setRouteError('');

      console.log(`✅ Loaded ${result.outlets.length} active outlets for route "${result.userRoute.name}"`);
      console.log(`📊 Visit Status: ${visits.length}/${result.outlets.length} outlets visited`);
      console.log(`📊 All outlets:`, result.outlets.map(o => ({ name: o.name, sequence: o.outletSequence, status: o.status })));

    } catch (error) {
      console.error('Error loading route data:', error);
      setRouteError('Error loading route data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Subscribe to real-time route (van) inventory when route is known
  useEffect(() => {
    if (!userRoute?.routeId) return;
    const q = query(
      collection(db, 'inventory'),
      where('warehouseId', '==', userRoute.routeId)
    );
    const unsub = onSnapshot(q, (snapshot) => {
      const inv: { [pid: string]: number } = {};
      snapshot.docs.forEach((doc) => {
        const d = doc.data() as any;
        inv[d.productId] = d.closingStock || 0;
      });
      setRouteInventory(inv);
    });
    return () => unsub();
  }, [userRoute?.routeId]);

  // Initialize visible product list from current van inventory once per outlet selection
  useEffect(() => {
    if (!selectedOutlet) {
      // Reset when outlet changes/clears
      setVisibleProductIds(new Set());
      setInitializedVisibleFromInventory(false);
      return;
    }
    if (!initializedVisibleFromInventory && salesDetails.length > 0) {
      // Choose products present in van inventory as defaults
      const defaults = new Set<string>();
      Object.entries(routeInventory).forEach(([pid, qty]) => {
        if ((qty || 0) > 0) defaults.add(pid);
      });
      if (defaults.size > 0) {
        setVisibleProductIds(defaults);
        setInitializedVisibleFromInventory(true);
      }
    }
  }, [selectedOutlet, salesDetails, routeInventory, initializedVisibleFromInventory]);

  // Compute the list to render based on visibility controls
  const displayedDetails = useMemo(() => {
    if (visibleProductIds.size === 0) return [] as SalesDetail[];
    const set = visibleProductIds;
    return salesDetails.filter(d => set.has(d.productId));
  }, [salesDetails, visibleProductIds]);

  // Items available to add via picker
  const addableItems = useMemo(() => {
    const set = visibleProductIds;
    return salesDetails
      .filter(d => !set.has(d.productId))
      .sort((a, b) => a.productName.localeCompare(b.productName));
  }, [salesDetails, visibleProductIds]);

  const handleOutletSelect = async (outlet: Outlet) => {
    setSelectedOutlet(outlet);
    setLoadingSalesDetails(true);
    
    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      
      // Create outlet visit record
      if (currentUser?.id && userRoute?.routeId) {
        const visitResult = await createOutletVisit(
          outlet.id,
          outlet.name || 'Unknown Outlet',
          currentUser.id,
          currentUser.name || 'Unknown Salesman',
          userRoute.routeId,
          today
        );
        
        if (visitResult.success) {
          console.log(`✅ Visit recorded for outlet: ${outlet.name}`);
          
          // Update local visit state
          setOutletVisits(prev => ({
            ...prev,
            [outlet.id]: {
              id: visitResult.visitId || '',
              date: today,
              routeId: userRoute.routeId,
              outletId: outlet.id,
              outletName: outlet.name || 'Unknown Outlet',
              salesmanId: currentUser.id,
              salesmanName: currentUser.name || 'Unknown Salesman',
              visitStartTime: new Date(),
              status: 'visit_only'
            }
          }));
          
          // Update visit stats
          setVisitStats(prev => ({
            ...prev,
            visited: Math.max(prev.visited, Object.keys(outletVisits).length + 1)
          }));
        }
      }
      
      const details = await getSalesDetailsForOutlet(outlet.id, today, todayPricing);
      setSalesDetails(details);
    } catch (error) {
      console.error('Error loading sales details:', error);
      alert('Error loading sales data for this outlet. Please ensure daily sales have been prepared.');
      setSalesDetails([]);
    } finally {
      setLoadingSalesDetails(false);
    }
  };

  const updateSaleQty = (detailId: string, quantity: number) => {
    setSalesDetails(salesDetails.map(detail => {
      if (detail.id === detailId) {
        const saleQty = Math.max(0, quantity);
        const lineTotal = saleQty * detail.unitRate;
        return { ...detail, saleQty, lineTotal };
      }
      return detail;
    }));
  };

  const autoCalculateSaleQty = useCallback((detailId: string) => {
    setSalesDetails(prevDetails => prevDetails.map(detail => {
      if (detail.id === detailId && detail.shelfQtyReco > 0) {
        const saleQty = Math.max(0, detail.shelfQtyReco - detail.openingStock - detail.replaceQty);
        const lineTotal = saleQty * detail.unitRate;
        return { ...detail, saleQty, lineTotal };
      }
      return detail;
    }));
  }, []);

  // Debounced auto-calculate function
  const debouncedAutoCalculate = useCallback((detailId: string, delay = 300) => {
    // Clear existing timer for this field
    if (debounceTimers[detailId]) {
      clearTimeout(debounceTimers[detailId]);
    }
    
    // Set new timer
    const timer = setTimeout(() => {
      autoCalculateSaleQty(detailId);
    }, delay);
    
    setDebounceTimers(prev => ({...prev, [detailId]: timer}));
  }, [debounceTimers, autoCalculateSaleQty]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimers).forEach(timer => clearTimeout(timer));
    };
  }, [debounceTimers]);

  const updateOpeningStock = (detailId: string, quantity: number) => {
    setSalesDetails(salesDetails.map(detail => {
      if (detail.id === detailId) {
        const openingStock = Math.max(0, quantity);
        const lineTotal = detail.saleQty * detail.unitRate;
        return { ...detail, openingStock, lineTotal };
      }
      return detail;
    }));
    // Trigger debounced auto-calculation for seeded records
    debouncedAutoCalculate(detailId);
  };

  const updateReplaceQty = (detailId: string, quantity: number) => {
    setSalesDetails(salesDetails.map(detail => {
      if (detail.id === detailId) {
        const replaceQty = Math.max(0, quantity);
        const lineTotal = detail.saleQty * detail.unitRate;
        return { ...detail, replaceQty, lineTotal };
      }
      return detail;
    }));
    // Trigger debounced auto-calculation for seeded records
    debouncedAutoCalculate(detailId);
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>, detailId: string) => {
    // Try to position cursor at end, fallback gracefully for number inputs
    try {
      if (e.target.type !== 'number') {
        e.target.setSelectionRange(e.target.value.length, e.target.value.length);
      }
    } catch (error) {
      // Silently handle browsers that don't support setSelectionRange on number inputs
    }
    // Auto-calculate sale qty on focus for seeded records
    autoCalculateSaleQty(detailId);
  };

  const handleSaleQtyFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Try to position cursor at end, fallback gracefully for number inputs
    try {
      if (e.target.type !== 'number') {
        e.target.setSelectionRange(e.target.value.length, e.target.value.length);
      }
    } catch (error) {
      // Silently handle browsers that don't support setSelectionRange on number inputs
    }
  };

  const getGrandTotal = () => {
    // Only sum totals for displayed items (what the user can sell)
    const source = displayedDetails.length > 0 ? displayedDetails : salesDetails;
    return source.reduce((sum, detail) => sum + detail.lineTotal, 0);
  };

  const getTotalItemsSold = () => {
    const source = displayedDetails.length > 0 ? displayedDetails : salesDetails;
    return source.reduce((sum, detail) => sum + detail.saleQty, 0);
  };

  const handleSaveSale = async () => {
    if (!selectedOutlet || (displayedDetails.length === 0 && salesDetails.length === 0)) {
      alert('Please select an outlet and ensure sales data is loaded');
      return;
    }

    // Check if any items have been sold
  const workingSet = displayedDetails.length > 0 ? displayedDetails : salesDetails;
  const itemsWithSales = workingSet.filter(detail => detail.saleQty > 0 || detail.replaceQty > 0);
    if (itemsWithSales.length === 0) {
      alert('Please enter sale quantities for at least one item');
      return;
    }

    setSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      
      const result = await saveSaleTransaction(
        workingSet,
        selectedOutlet.id,
        selectedOutlet.name || 'Unknown Outlet',
        currentUser?.id || '',
        currentUser?.name || '',
        today,
        additionalDiscount
      );

      if (result.success) {
        // Update outlet visit with transaction details
  const totalSaleQty = workingSet.reduce((sum, detail) => sum + detail.saleQty, 0);
  const totalReplaceQty = workingSet.reduce((sum, detail) => sum + detail.replaceQty, 0);
        const totalSaleAmount = Math.max(0, getGrandTotal() - additionalDiscount);

        if (currentUser?.id) {
          await updateOutletVisitWithTransaction(
            selectedOutlet.id,
            currentUser.id,
            today,
            {
              saleAmount: totalSaleAmount,
              replaceQty: totalReplaceQty,
              totalSaleQty
            }
          );
        }

        // Reset the form
        setSalesDetails([]);
        setAdditionalDiscount(0);
        setSelectedOutlet(null);
        onBack();
      } else {
        alert(`Error saving sale: ${result.message}`);
      }
    } catch (error) {
      console.error('Error saving sale:', error);
      alert('Error saving sale. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleNoSaleSubmit = async (reason: string, photoUrl?: string) => {
    if (!selectedOutlet || !currentUser?.id) {
      alert('Error: Missing outlet or user information');
      return;
    }

    setSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0];

      // Update outlet visit with no-sale information
      if (currentUser?.id) {
        await updateOutletVisitWithTransaction(
          selectedOutlet.id,
          currentUser.id,
          today,
          {
            status: 'no_sale',
            noSaleReason: reason as any,
            shelfPhotoUrl: photoUrl,
            saleAmount: 0,
            replaceQty: 0,
            totalSaleQty: 0
          }
        );
      }

      alert(`No sale reason recorded: ${reason}`);
      
      // Reset and go back
      setShowNoSaleModal(false);
      setSalesDetails([]);
      setSelectedOutlet(null);
      onBack();
    } catch (error) {
      console.error('Error recording no sale:', error);
      alert('Error recording no sale. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || pricingLoading) {
    return (
      <div className="mobile-container bg-gray-50 min-h-screen">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <ShoppingCart className="w-12 h-12 text-gray-400 mx-auto mb-4 animate-pulse" />
            <p className="text-gray-600">
              {loading ? 'Loading sales...' : 'Loading pricing data...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-container bg-gray-50 min-h-screen">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-3">
            <button
              onClick={onBack}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center space-x-2">
              <ShoppingCart className="w-6 h-6 text-green-600" />
              <h1 className="text-lg font-semibold text-gray-800">
                {editMode ? 'Edit Sale' : 'New Sale'}
              </h1>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Total:</span>
            <span className="text-lg font-bold text-green-600">
              ₹{getGrandTotal().toLocaleString()}
            </span>
          </div>
        </div>
      </header>

      <div className="p-4 space-y-4">
        {/* Route Error Display */}
        {routeError && (
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <div className="flex items-center space-x-2 text-red-700">
              <TrendingUp className="w-5 h-5" />
              <span className="font-medium">Route Assignment Issue</span>
            </div>
            <p className="text-sm text-red-600 mt-1">{routeError}</p>
          </div>
        )}

        {/* Route Info Display */}
        {userRoute && !routeError && (
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-blue-700">
                <TrendingUp className="w-5 h-5" />
                <span className="font-medium">Today's Route: {userRoute.name}</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-blue-600">
                <Eye className="w-4 h-4" />
                <span>{visitStats.visited}/{visitStats.total} visited</span>
              </div>
            </div>
            <p className="text-sm text-blue-600 mt-1">{outlets.length} outlets available for sales</p>
          </div>
        )}

        {/* Outlet Selection */}
        {!routeError && (
          <div className="bg-white p-4 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Select Outlet</h3>
          {selectedOutlet ? (
            <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-gray-800">{selectedOutlet.name}</p>
                  <p className="text-sm text-gray-600">{selectedOutlet.address}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedOutlet(null);
                  setSalesDetails([]);
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Change
              </button>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {outlets.map(outlet => {
                const visit = outletVisits[outlet.id];
                const isVisited = !!visit;
                const hasSale = visit?.status === 'sale' || visit?.status === 'sale_and_replace';
                
                return (
                  <button
                    key={outlet.id}
                    onClick={() => handleOutletSelect(outlet)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                      isVisited 
                        ? 'bg-green-50 border border-green-200 hover:bg-green-100' 
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {outlet.outletSequence && (
                        <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                          {outlet.outletSequence}
                        </div>
                      )}
                      {isVisited ? (
                        <CheckCircle className={`w-5 h-5 ${hasSale ? 'text-green-600' : 'text-blue-600'}`} />
                      ) : (
                        <MapPin className="w-5 h-5 text-gray-500" />
                      )}
                      <div className="text-left">
                        <p className="font-medium text-gray-800">{outlet.name || 'Unnamed Outlet'}</p>
                        <p className="text-sm text-gray-600">{outlet.address}</p>
                        {isVisited && (
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`text-xs px-2 py-1 rounded ${
                              hasSale 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {visit.status === 'sale' && 'Sale Completed'}
                              {visit.status === 'sale_and_replace' && 'Sale + Replace'}
                              {visit.status === 'replace_only' && 'Replace Only'}
                              {visit.status === 'visit_only' && 'Visited'}
                            </span>
                            {hasSale && visit.saleAmount && (
                              <span className="text-xs text-green-600 font-medium">
                                ₹{visit.saleAmount.toLocaleString()}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          </div>
        )}

        {/* Sales Details Table */}
        {selectedOutlet && (
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Sales Details</h3>
              <div className="flex items-center space-x-2">
                {getTotalItemsSold() > 0 && (
                  <div className="flex items-center space-x-2 text-sm text-green-600">
                    <Package className="w-4 h-4" />
                    <span>{getTotalItemsSold()} items selected</span>
                  </div>
                )}
                <button
                  onClick={() => setShowAddItemPicker(true)}
                  className="text-sm px-3 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                >
                  + Add item
                </button>
              </div>
            </div>

            {loadingSalesDetails ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-gray-600">Loading sales data...</p>
              </div>
            ) : (displayedDetails.length === 0 && salesDetails.length === 0) ? (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No sales data available for this outlet</p>
                <p className="text-sm mt-1">Please ensure daily sales have been prepared</p>
              </div>
            ) : displayedDetails.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-gray-700">No van items to show.</p>
                <p className="text-sm text-gray-500">Use 
                  <button onClick={() => setShowAddItemPicker(true)} className="mx-1 text-blue-600 underline">+ Add item</button>
                  to select products.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {displayedDetails.map(detail => (
                  <div key={detail.id} className="p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-800">{detail.productName}</h4>
                      <div className="flex items-center gap-2">
                        <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          Shelf Space: {detail.shelfQtyReco}
                        </div>
                        <div className={`text-xs px-2 py-1 rounded ${
                          (routeInventory[detail.productId] ?? 0) > 0
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          Van: {routeInventory[detail.productId] ?? 0}
                        </div>
                      </div>
                    </div>
                    
                    {/* Show different UI based on whether this is seeded or fallback data */}
                    {detail.shelfQtyReco > 0 ? (
                      // SEEDED RECORDS: Show target and auto-calculate
                      <>
                        <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                          <div>
                            <label className="block text-gray-600 mb-1">Shelf Qty Target</label>
                            <div className="flex items-center space-x-1">
                              <Package className="w-3 h-3 text-blue-400" />
                              <span className="font-medium text-blue-600">{detail.shelfQtyReco}</span>
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-gray-600 mb-1">Rate per unit</label>
                            <div className="flex items-center space-x-1">
                              <Calculator className="w-3 h-3 text-gray-400" />
                              <span className="font-medium">₹{detail.unitRate}</span>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                          <div>
                            <label className="block text-gray-600 mb-1">Opening Stock</label>
                            <input
                              type="number"
                              min="0"
                              value={detail.openingStock === 0 ? '' : detail.openingStock}
                              onChange={(e) => updateOpeningStock(detail.id, parseInt(e.target.value) || 0)}
                              onFocus={(e) => handleInputFocus(e, detail.id)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Enter opening stock"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-gray-600 mb-1">Replace Qty</label>
                            <input
                              type="number"
                              min="0"
                              value={detail.replaceQty === 0 ? '' : detail.replaceQty}
                              onChange={(e) => updateReplaceQty(detail.id, parseInt(e.target.value) || 0)}
                              onFocus={(e) => handleInputFocus(e, detail.id)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Enter replacement qty"
                            />
                          </div>
                        </div>

                        <div className="text-sm mb-3">
                          <div>
                            <label className="block text-gray-600 mb-1">Sale Qty</label>
                            <input
                              type="number"
                              min="0"
                              value={detail.saleQty === 0 ? '' : detail.saleQty}
                              onChange={(e) => updateSaleQty(detail.id, parseInt(e.target.value) || 0)}
                              onFocus={handleSaleQtyFocus}
                              className="w-full px-2 py-1 border border-green-300 rounded text-sm focus:ring-1 focus:ring-green-500 focus:border-transparent"
                              placeholder="Enter sale qty or auto-calculated"
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      // FALLBACK RECORDS: Manual entry mode
                      <>
                        <div className="bg-yellow-50 border border-yellow-200 p-2 rounded text-xs mb-3">
                          <div className="flex items-center space-x-1 text-yellow-700">
                            <Package className="w-3 h-3" />
                            <span className="font-medium">Manual Entry Mode</span>
                          </div>
                          <p className="text-yellow-600 mt-1">No daily seeding found. Enter quantities manually.</p>
                        </div>

                        <div className="grid grid-cols-3 gap-3 text-sm mb-3">
                          <div>
                            <label className="block text-gray-600 mb-1">Opening Stock</label>
                            <input
                              type="number"
                              min="0"
                              value={detail.openingStock === 0 ? '' : detail.openingStock}
                              onChange={(e) => updateOpeningStock(detail.id, parseInt(e.target.value) || 0)}
                              onFocus={handleSaleQtyFocus}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Enter opening stock"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-gray-600 mb-1">Replace Qty</label>
                            <input
                              type="number"
                              min="0"
                              value={detail.replaceQty === 0 ? '' : detail.replaceQty}
                              onChange={(e) => updateReplaceQty(detail.id, parseInt(e.target.value) || 0)}
                              onFocus={handleSaleQtyFocus}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Enter replacement qty"
                            />
                          </div>

                          <div>
                            <label className="block text-gray-600 mb-1">Sale Qty</label>
                            <input
                              type="number"
                              min="0"
                              value={detail.saleQty === 0 ? '' : detail.saleQty}
                              onChange={(e) => updateSaleQty(detail.id, parseInt(e.target.value) || 0)}
                              onFocus={handleSaleQtyFocus}
                              className="w-full px-2 py-1 border border-green-300 rounded text-sm focus:ring-1 focus:ring-green-500 focus:border-transparent"
                              placeholder="Enter sale qty"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-3 text-sm">
                          <div>
                            <label className="block text-gray-600 mb-1">Rate per unit</label>
                            <div className="flex items-center space-x-1">
                              <Calculator className="w-3 h-3 text-gray-400" />
                              <span className="font-medium">₹{detail.unitRate}</span>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {detail.lineTotal > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Line Total:</span>
                          <span className="font-semibold text-green-600">₹{detail.lineTotal.toLocaleString()}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Grand Total */}
        {selectedOutlet && (displayedDetails.length > 0 || salesDetails.length > 0) && getGrandTotal() > 0 && (
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Items Sold:</span>
                <span className="font-medium">{getTotalItemsSold()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">₹{getGrandTotal().toLocaleString()}</span>
              </div>
              
              {/* Additional Discount Field */}
              <div className="flex items-center justify-between">
                <label className="text-gray-600">Additional Discount:</label>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">₹</span>
                  <input
                    type="number"
                    value={additionalDiscount || ''}
                    onChange={(e) => setAdditionalDiscount(Number(e.target.value) || 0)}
                    placeholder="0"
                    min="0"
                    max={getGrandTotal()}
                    className="w-24 px-2 py-1 text-right border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              {/* Final Grand Total */}
              <div className="border-t pt-2">
                <div className="flex items-center justify-between text-lg font-semibold">
                  <span className="text-gray-800">Grand Total:</span>
                  <span className="text-green-600">₹{Math.max(0, getGrandTotal() - additionalDiscount).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {selectedOutlet && (displayedDetails.length > 0 || salesDetails.length > 0) && (
          <div className="space-y-3">
            {/* Save Sale Button */}
            <button
              onClick={handleSaveSale}
              disabled={getGrandTotal() === 0 || saving}
              className={`w-full py-3 rounded-lg font-medium transition-colors ${
                getGrandTotal() > 0 && !saving
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {saving ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Saving Sale...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center space-x-2">
                  <Save className="w-5 h-5" />
                  <span>Save Sale (₹{Math.max(0, getGrandTotal() - additionalDiscount).toLocaleString()})</span>
                </div>
              )}
            </button>

            {/* No Sale Button */}
            <button
              onClick={() => setShowNoSaleModal(true)}
              disabled={saving}
              className="w-full py-3 border-2 border-orange-500 text-orange-600 rounded-lg font-medium hover:bg-orange-50 transition-colors disabled:opacity-50"
            >
              <div className="flex items-center justify-center space-x-2">
                <XCircle className="w-5 h-5" />
                <span>No Sale</span>
              </div>
            </button>
          </div>
        )}

        {/* No Sale Reason Modal */}
        <NoSaleReasonModal
          isOpen={showNoSaleModal}
          onClose={() => setShowNoSaleModal(false)}
          onSubmit={handleNoSaleSubmit}
          outletName={selectedOutlet?.name || 'Selected Outlet'}
        />

        {/* Add Item Picker Modal */}
        {showAddItemPicker && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Add Products</h3>
                  {selectedOutlet && (
                    <p className="text-sm text-gray-600">{selectedOutlet.name}</p>
                  )}
                </div>
                <button
                  onClick={() => setShowAddItemPicker(false)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 space-y-3">
                <input
                  value={addItemSearch}
                  onChange={(e) => setAddItemSearch(e.target.value)}
                  placeholder="Search products"
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
                <div className="max-h-[50vh] overflow-y-auto divide-y border border-gray-200 rounded">
                  {addableItems
                    .filter(it => it.productName.toLowerCase().includes(addItemSearch.toLowerCase()))
                    .map(it => (
                      <button
                        key={it.productId}
                        className="w-full text-left px-3 py-3 hover:bg-gray-50 flex items-center justify-between"
                        onClick={() => {
                          setVisibleProductIds(prev => new Set([...Array.from(prev), it.productId]));
                          setShowAddItemPicker(false);
                          setInitializedVisibleFromInventory(true);
                        }}
                      >
                        <div>
                          <div className="font-medium text-gray-800">{it.productName}</div>
                          <div className="text-xs text-gray-500">Van: {routeInventory[it.productId] ?? 0}</div>
                        </div>
                        <span className="text-blue-600 text-sm">Add</span>
                      </button>
                    ))}
                  {addableItems.length === 0 && (
                    <div className="text-center text-gray-500 py-6">No more products to add</div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-gray-200">
                <button
                  onClick={() => setShowAddItemPicker(false)}
                  className="w-full py-2 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sales;
